import { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import { apiGet, apiPatch, apiPost } from "lib/apiClient";
import { DESCRIPTION_MAX, SITE_PALETTES, isHexColour, paletteFor, readableOn } from "lib/partnerPages";
import { ColourField, LogoField } from "../partners/PartnerProfileEditor";

const textareaClass =
  "mt-2 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50";

function errorMessage(err, fallback) {
  if (err?.code === "INVALID_BRAND_COLOUR") return "Colours must look like #1a2b3c.";
  if (err?.code === "UNSUPPORTED_IMAGE_TYPE") return "Use a PNG, JPEG, WebP or GIF logo.";
  if (err?.code === "IMAGE_TOO_LARGE") return "That logo is too large. Use an image under 5 MB.";
  return err?.message || fallback;
}

function Preview({ page, primary, accent }) {
  const effectivePrimary = isHexColour(primary) ? primary : page.defaults.primaryColor || "#023e97";
  const effectiveAccent = isHexColour(accent) ? accent : page.defaults.accentColor || effectivePrimary;
  const logo = page.profile.logoUrl || page.profile.logoDarkUrl || page.defaults.logoUrl || page.defaults.logoDarkUrl;
  const usingTenantLogo = !page.profile.logoUrl && !page.profile.logoDarkUrl;
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-navy-800">
        <div className="flex min-w-0 items-center gap-2">
          {logo ? <img src={logo} alt="" className="h-7 max-w-[140px] object-contain" /> : <span className="truncate text-sm font-bold text-navy-700 dark:text-white">{page.defaults.name}</span>}
        </div>
        <span className="rounded-md px-3 py-1.5 text-xs font-bold" style={{ background: effectivePrimary, color: readableOn(effectivePrimary) }}>
          Sign up
        </span>
      </div>
      <div className="space-y-3 bg-gray-50 p-4 dark:bg-navy-900">
        <div className="rounded-lg border-t-4 bg-white p-3 dark:bg-navy-800" style={{ borderTopColor: effectivePrimary }}>
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: effectiveAccent }}>
            {page.partnerName}
          </p>
          <p className="mt-1 text-sm font-semibold text-navy-700 dark:text-white">Will Harambee Stars qualify for AFCON?</p>
          <div className="mt-3 flex gap-2">
            <span className="flex-1 rounded-md py-1.5 text-center text-xs font-bold" style={{ background: effectivePrimary, color: readableOn(effectivePrimary) }}>
              Yes 62%
            </span>
            <span className="flex-1 rounded-md border border-gray-200 py-1.5 text-center text-xs font-bold text-navy-700 dark:border-white/10 dark:text-white">No 38%</span>
          </div>
        </div>
        <p className="text-center text-[11px] text-gray-500">Powered by {page.effective.poweredBy.name}</p>
      </div>
      {usingTenantLogo && <p className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-white/10">Showing {page.defaults.name}&apos;s logo until you upload yours.</p>}
    </div>
  );
}

/**
 * The partner's own site: its address, logo, colours and description. The site starts in
 * the tenant's look; everything here is the partner's to change except the address, which
 * only the tenant sets.
 */
export default function PartnerSitePage() {
  const [page, setPage] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [primary, setPrimary] = useState("");
  const [accent, setAccent] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const adopt = useCallback((next) => {
    setPage(next);
    setPrimary(next.profile.brandPrimary || "");
    setAccent(next.profile.brandAccent || "");
    setDescription(next.profile.description || "");
  }, []);

  useEffect(() => {
    apiGet("/portal/partner/page")
      .then((res) => adopt(res.page))
      .catch((err) => setLoadError(errorMessage(err, "Could not load your site settings.")));
  }, [adopt]);

  if (loadError) return <p className="mt-5 text-sm font-medium text-red-500">{loadError}</p>;
  if (!page) return <p className="mt-5 text-sm text-gray-500">Loading…</p>;

  const dirty =
    (primary || null) !== (page.profile.brandPrimary || null) ||
    (accent || null) !== (page.profile.brandAccent || null) ||
    (description.trim() || null) !== (page.profile.description || null);
  const activePalette = paletteFor(primary, accent);

  async function save() {
    setError(null);
    setMessage(null);
    if (primary && !isHexColour(primary)) return setError("The primary colour must look like #1a2b3c.");
    if (accent && !isHexColour(accent)) return setError("The accent colour must look like #1a2b3c.");
    setBusy(true);
    try {
      const res = await apiPatch("/portal/partner/page", {
        brandPrimary: primary ? primary.toLowerCase() : null,
        brandAccent: accent ? accent.toLowerCase() : null,
        description: description.trim() || null,
      });
      adopt(res.page);
      setMessage("Saved. Your site shows the new look within a minute.");
    } catch (err) {
      setError(errorMessage(err, "Could not save your site."));
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(variant, file) {
    setError(null);
    setMessage(null);
    setLogoBusy(variant);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await apiPost(`/portal/partner/page/logo?variant=${variant}`, body);
      setPage(res.page);
      setMessage("Logo updated.");
    } catch (err) {
      setError(errorMessage(err, "Could not upload that logo."));
    } finally {
      setLogoBusy(null);
    }
  }

  async function removeLogo(variant) {
    setError(null);
    setMessage(null);
    setLogoBusy(variant);
    try {
      const res = await apiPatch("/portal/partner/page", variant === "dark" ? { logoDarkUrl: null } : { logoUrl: null });
      setPage(res.page);
      setMessage("Logo removed.");
    } catch (err) {
      setError(errorMessage(err, "Could not remove that logo."));
    } finally {
      setLogoBusy(null);
    }
  }

  function copyAddress() {
    if (!page.siteUrl) return;
    navigator.clipboard?.writeText(page.siteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
      <div className="space-y-5 xl:col-span-2">
        <Card extra="p-6">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">Your site</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Your clients trade here: {page.defaults.name}&apos;s markets plus the markets you create, under your name. Share this address with them.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-lightPrimary px-3 py-2 text-sm font-semibold text-navy-700 dark:bg-white/5 dark:text-white">{page.siteUrl?.replace("https://", "") || "No address yet"}</code>
            {page.siteUrl && (
              <>
                <button type="button" onClick={copyAddress} className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-bold text-white hover:bg-brand-600">
                  {copied ? "Copied" : "Copy address"}
                </button>
                <a href={page.siteUrl} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700">
                  Open site
                </a>
              </>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">The address is set by {page.defaults.name}. Ask them if it needs to change.</p>
        </Card>

        <Card extra="p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Logo</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Shown in your site&apos;s header and footer. Until you upload one, your site uses {page.defaults.name}&apos;s logo.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            <LogoField
              label="For light backgrounds"
              hint="PNG, JPEG, WebP or GIF, up to 5 MB. A wide logo about 4:1 looks best."
              url={page.profile.logoUrl}
              busy={logoBusy === "light"}
              onPick={(file) => uploadLogo("light", file)}
              onRemove={() => removeLogo("light")}
            />
            <LogoField
              label="For dark backgrounds"
              hint="Optional: a light version for dark mode. Without it, the light-background logo is used."
              url={page.profile.logoDarkUrl}
              dark
              busy={logoBusy === "dark"}
              onPick={(file) => uploadLogo("dark", file)}
              onRemove={() => removeLogo("dark")}
            />
          </div>
        </Card>

        <Card extra="p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Colours</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Pick a ready-made palette, or mix your own. The primary colour is for buttons and links, the accent for highlights.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {SITE_PALETTES.map((palette) => (
              <button
                key={palette.id}
                type="button"
                onClick={() => {
                  setPrimary(palette.primary);
                  setAccent(palette.accent);
                  setMessage(null);
                }}
                aria-pressed={activePalette?.id === palette.id}
                className={`flex items-center gap-2 rounded-lg border p-2 text-left text-xs font-semibold text-navy-700 transition dark:text-white ${
                  activePalette?.id === palette.id ? "border-brand-500 ring-2 ring-brand-500/30" : "border-gray-200 hover:border-gray-300 dark:border-white/10"
                }`}
              >
                <span className="flex shrink-0 overflow-hidden rounded-md">
                  <span className="h-6 w-5" style={{ background: palette.primary }} />
                  <span className="h-6 w-5" style={{ background: palette.accent }} />
                </span>
                {palette.name}
              </button>
            ))}
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
            <ColourField label="Primary colour" value={primary} onChange={(v) => setPrimary(v)} />
            <ColourField label="Accent colour" value={accent} onChange={(v) => setAccent(v)} />
          </div>
          {(primary || accent) && (
            <button
              type="button"
              onClick={() => {
                setPrimary("");
                setAccent("");
              }}
              className="mt-3 text-xs font-semibold text-gray-500 hover:text-navy-700 dark:hover:text-white"
            >
              Use {page.defaults.name}&apos;s colours instead
            </button>
          )}
        </Card>

        <Card extra="p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">About you</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">A line or two at the top of your site&apos;s home page.</p>
          <textarea className={textareaClass} rows={3} maxLength={DESCRIPTION_MAX} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Kenyan football, priced by our members." />
          <p className="mt-1 text-right text-xs text-gray-500">
            {description.length}/{DESCRIPTION_MAX}
          </p>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={save} disabled={busy || !dirty} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-bold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? "Saving…" : "Save changes"}
          </button>
          {dirty && !busy && (
            <button type="button" onClick={() => adopt(page)} className="text-sm font-semibold text-gray-500 hover:text-navy-700 dark:hover:text-white">
              Discard
            </button>
          )}
          {message && <p className="text-sm font-medium text-green-600">{message}</p>}
          {error && <p className="text-sm font-medium text-red-500">{error}</p>}
        </div>
      </div>

      <div className="xl:sticky xl:top-5 xl:self-start">
        <Card extra="p-5">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">Preview</h3>
          <Preview page={page} primary={primary} accent={accent} />
        </Card>
      </div>
    </div>
  );
}
